import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { CreditCard } from "lucide-react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import { Alert, AlertDescription } from "@lawline/ui/components/alert";
import { Skeleton } from "@lawline/ui/components/skeleton";
import { Spinner } from "@lawline/ui/components/spinner";

import { authClient } from "@platform/auth/client";
import { billingQueryOptions } from "./billing.queries";
import { BillingInfo } from "./components/billing-info";
import { BillingPlan } from "./components/billing-plan";
import { BillingPriceTable } from "./components/billing-price-table";
import { BillingSpend } from "./components/billing-spend";
import { EnterpriseRequestDialog } from "./components/enterprise-request";

export function BillingPage() {
	const t = useTranslations("billing");
	const { data: tenant } = authClient.useActiveOrganization();
	const status = useQuery(billingQueryOptions());
	const billing = status.data;
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState(false);

	const manageExisting =
		billing?.billingStatus === "active" || billing?.billingStatus === "past_due";
	const loading = status.isPending || status.isFetching;
	const known =
		!status.isError &&
		billing != null &&
		["active", "pending", "past_due", "canceled"].includes(billing.billingStatus);
	const ready = known && !loading;

	async function handleBilling() {
		if (!tenant?.id || !ready || busy) return;
		setBusy(true);
		setFailed(false);
		const returnUrl = `${window.location.origin}/billing`;
		try {
			const res = manageExisting
				? await authClient.subscription.billingPortal({
						customerType: "organization",
						disableRedirect: true,
						referenceId: tenant.id,
						returnUrl,
					})
				: await authClient.subscription.upgrade({
						customerType: "organization",
						disableRedirect: true,
						plan: "metered",
						referenceId: tenant.id,
						successUrl: returnUrl,
						cancelUrl: returnUrl,
					});
			const url = (res?.data as { url?: string } | null | undefined)?.url;
			if (res.error || !url) throw new Error("Billing session unavailable");
			window.location.assign(url);
		} catch {
			setFailed(true);
			setBusy(false);
		}
	}

	if (loading && !known) {
		return (
			<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				<div role="status">
					<Skeleton className="h-32 w-full" />
				</div>
			</section>
		);
	}

	if (!known) {
		return (
			<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				<Alert variant="destructive">
					<AlertDescription>{t("statusError")}</AlertDescription>
				</Alert>
				<Button className="self-start" variant="outline" onClick={() => void status.refetch()}>
					{t("retryStatus")}
				</Button>
			</section>
		);
	}

	return (
		<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6">
			<h1 className="text-2xl font-semibold">{t("title")}</h1>
			{failed && (
				<p role="alert" className="text-destructive text-sm">
					{t("sessionError")}
				</p>
			)}
			{billing?.billingStatus === "past_due" && (
				<Alert variant="destructive">
					<AlertDescription>{t("pastDueNotice")}</AlertDescription>
				</Alert>
			)}

			{manageExisting && billing ? (
				<div className="flex flex-col gap-8">
					<BillingSpend />
					<BillingInfo />
					<BillingPlan overview={billing} />
				</div>
			) : (
				<BillingPriceTable />
			)}

			<div className="flex gap-4">
				<Button
					className="self-start"
					onClick={handleBilling}
					disabled={busy || !tenant?.id || !ready}
				>
					{busy && <Spinner />}
					{manageExisting ? (
						t("manageBilling")
					) : (
						<span className="flex items-center gap-2">
							<CreditCard className="size-4" />
							{t("addPaymentMethod")}
						</span>
					)}
				</Button>

				{billing?.rateTier !== "enterprise" && <EnterpriseRequestDialog />}
			</div>
		</section>
	);
}
